import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Button, Heading, Spinner, Text, VStack } from '@chakra-ui/react'
import { CsatPublicLayout } from '@/components/csat/CsatPublicLayout'
import { CsatStarRating } from '@/components/csat/CsatStarRating'
import { getCsatSurveyStatus, submitCsatByToken } from '@/lib/services/csatService'
import { normalizeCsatScore, type CsatScore } from '@/types/csat'

type SurveyState = 'loading' | 'ready' | 'expired' | 'invalid'

export default function CsatPage() {
  const { token } = useParams<{ token: string }>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const ratingParam = searchParams.get('rating') ?? searchParams.get('score')
  const autoSubmitted = useRef(false)

  const [surveyState, setSurveyState] = useState<SurveyState>('loading')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [pendingScore, setPendingScore] = useState<CsatScore | null>(
    normalizeCsatScore(ratingParam)
  )

  const submitScore = useCallback(
    async (score: CsatScore) => {
      if (!token) return
      setLoading(true)
      setError(null)
      try {
        const res = await submitCsatByToken(token, score)
        const alreadyRated = res.message.toLowerCase().includes('already')
        navigate('/csat/thank-you', { replace: true, state: { alreadyRated } })
      } catch (err: unknown) {
        const response = (err as { response?: { status?: number; data?: { message?: string } } })
          ?.response
        const msg = response?.data?.message
        if (response?.status === 410 || msg?.toLowerCase().includes('expired')) {
          setSurveyState('expired')
          setError(msg ?? 'This survey link has expired.')
          return
        }
        if (msg?.toLowerCase().includes('already')) {
          navigate('/csat/thank-you', { replace: true, state: { alreadyRated: true } })
          return
        }
        setError(msg ?? 'Something went wrong. Please try again.')
      } finally {
        setLoading(false)
      }
    },
    [token, navigate]
  )

  useEffect(() => {
    if (!token) {
      setSurveyState('invalid')
      return
    }

    let cancelled = false

    void (async () => {
      try {
        const status = await getCsatSurveyStatus(token)
        if (cancelled) return

        if (status.expired) {
          setSurveyState('expired')
          setError(status.message)
          return
        }

        if (status.already_rated) {
          navigate('/csat/thank-you', { replace: true, state: { alreadyRated: true } })
          return
        }

        setSurveyState('ready')
      } catch (err: unknown) {
        if (cancelled) return
        const response = (err as { response?: { status?: number; data?: { message?: string } } })
          ?.response
        if (response?.status === 410) {
          setSurveyState('expired')
          setError(response.data?.message ?? 'This survey link has expired.')
        } else {
          setSurveyState('invalid')
          setError(response?.data?.message ?? 'Invalid survey link.')
        }
      }
    })()

    return () => {
      cancelled = true
    }
  }, [token, navigate])

  useEffect(() => {
    const normalized = normalizeCsatScore(ratingParam)
    if (
      surveyState !== 'ready' ||
      !token ||
      normalized == null ||
      autoSubmitted.current
    ) {
      return
    }
    autoSubmitted.current = true
    void submitScore(normalized)
  }, [surveyState, token, ratingParam, submitScore])

  if (
    surveyState === 'loading' ||
    (normalizeCsatScore(ratingParam) && surveyState === 'ready' && loading)
  ) {
    return (
      <CsatPublicLayout>
        <VStack spacing={4} py={8}>
          <Spinner size="lg" color="blue.500" />
          <Text color="gray.600">
            {normalizeCsatScore(ratingParam) ? 'Submitting your feedback…' : 'Loading survey…'}
          </Text>
          {error && (
            <Text color="red.500" fontSize="sm" textAlign="center">
              {error}
            </Text>
          )}
        </VStack>
      </CsatPublicLayout>
    )
  }

  if (surveyState === 'expired') {
    return (
      <CsatPublicLayout>
        <VStack spacing={4} py={8} textAlign="center">
          <Text fontSize="4xl" aria-hidden>
            ⏱
          </Text>
          <Heading size="md">Survey link expired</Heading>
          <Text color="gray.600" fontSize="sm">
            {error ??
              'This survey link is no longer valid. Survey links expire 30 days after the email was sent.'}
          </Text>
        </VStack>
      </CsatPublicLayout>
    )
  }

  if (surveyState === 'invalid') {
    return (
      <CsatPublicLayout>
        <VStack spacing={4} py={8} textAlign="center">
          <Heading size="md">Invalid survey link</Heading>
          <Text color="gray.600" fontSize="sm">
            {error ?? 'This link is not valid. Please use the link from your CSAT email.'}
          </Text>
        </VStack>
      </CsatPublicLayout>
    )
  }

  return (
    <CsatPublicLayout>
      <VStack spacing={6} align="stretch">
        <VStack spacing={1} textAlign="center">
          <Heading size="md">How did we do?</Heading>
          <Text color="gray.500" fontSize="sm">
            Please rate your support experience
          </Text>
        </VStack>

        <CsatStarRating value={pendingScore} onChange={setPendingScore} disabled={loading} />

        {error && (
          <Text color="red.500" fontSize="sm" textAlign="center">
            {error}
          </Text>
        )}

        <Button
          colorScheme="blue"
          size="lg"
          w="100%"
          isLoading={loading}
          isDisabled={pendingScore == null}
          onClick={() => pendingScore != null && void submitScore(pendingScore)}
        >
          Submit Rating
        </Button>
      </VStack>
    </CsatPublicLayout>
  )
}
